import { DateTime } from 'luxon';

/**
 * Regras de leitura da composição de uma venda.
 *
 * Mora fora do componente por dois motivos. O primeiro é que a aritmética do resumo merece
 * teste. O segundo é semântico: `amountLiquid` é bruto − taxas e **não** desconta o split
 * enviado a terceiro, então ele só coincide com o que cai na conta quando não há split de
 * saída. Cruzar o movimento com os balances é a única forma de dizer as duas coisas sem
 * mentir em nenhuma delas.
 *
 * Nada aqui formata dinheiro: `maskCurrency` lê uma flag global mutável e congela se o
 * resultado for memoizado. Estas funções devolvem números; quem formata é o render.
 */

export interface BreakdownBalance {
  id?: string;
  amount?: string | number | null;
  amountRefunded?: string | number | null;
  released?: boolean;
  releasedOn?: string | null;
  deletedAt?: string | null;
  Customer?: { id?: string; name?: string } | null;
}

export type SettlementState = 'canceled' | 'released' | 'waiting';

/** O backend devolve Decimal como string; `Number(null)` é 0 e `Number('x')` é NaN. */
export const toAmount = (value?: string | number | null): number => {
  if (value === null || value === undefined || value === '') return 0;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const settlementStateOf = (balance?: BreakdownBalance): SettlementState => {
  if (balance?.deletedAt) return 'canceled';
  return balance?.released ? 'released' : 'waiting';
};

/**
 * O que de fato credita o dono da venda: ignora linhas canceladas e as que pertencem a
 * outro cliente (split enviado para fora).
 */
export const creditedToOwner = (balances?: BreakdownBalance[], ownerId?: string): number =>
  (balances ?? [])
    .filter((balance) => settlementStateOf(balance) !== 'canceled')
    .filter((balance) => !ownerId || balance?.Customer?.id === ownerId)
    .reduce((total, balance) => total + toAmount(balance?.amount), 0);

/**
 * Guarda contra a coluna que só existe para mostrar `---`. Comparar por número e não por
 * truthiness: o backend devolve `"0.00"`, que é string não vazia e portanto verdadeira.
 */
export const hasAnyRefund = (balances?: BreakdownBalance[]): boolean =>
  (balances ?? []).some((balance) => toAmount(balance?.amountRefunded) > 0);

/**
 * O único valor distinto da coluna, ou `null` quando as linhas divergem.
 *
 * Coluna que repete o mesmo conteúdo em todas as linhas não é informação: sobe para o
 * subtítulo da seção e devolve a largura para as colunas que variam.
 */
export const sharedValue = <T>(items: T[] | undefined, pick: (item: T) => string | null | undefined): string | null => {
  const list = items ?? [];
  if (!list.length) return null;

  const first = pick(list[0]);
  if (!first) return null;

  return list.every((item) => pick(item) === first) ? first : null;
};

export const sharedSettlement = (
  balances?: BreakdownBalance[],
): { state: SettlementState; releasedOn: string | null } | null => {
  const list = balances ?? [];
  if (!list.length) return null;

  const key = sharedValue(list, (balance) => `${settlementStateOf(balance)}|${balance?.releasedOn ?? ''}`);
  if (!key) return null;

  return { state: settlementStateOf(list[0]), releasedOn: list[0]?.releasedOn ?? null };
};

/** A primeira liberação prevista entre as linhas que continuam de pé. */
export const earliestRelease = (balances?: BreakdownBalance[]): string | null =>
  (balances ?? [])
    .filter((balance) => settlementStateOf(balance) !== 'canceled')
    .map((balance) => balance?.releasedOn)
    .filter((releasedOn): releasedOn is string => !!releasedOn)
    .sort()[0] ?? null;

/**
 * O "D+7" da venda. Conta dias de calendário — uma venda às 22h de segunda que libera na
 * terça é D+1, não D+0 como sairia da diferença bruta de horas.
 */
export const settlementDays = (orderAt?: string | null, releasedOn?: string | null): number | null => {
  if (!orderAt || !releasedOn) return null;

  const start = DateTime.fromISO(orderAt);
  const end = DateTime.fromISO(releasedOn);
  if (!start.isValid || !end.isValid) return null;

  const days = end.startOf('day').diff(start.startOf('day'), 'days').days;
  return days < 0 ? null : Math.round(days);
};
